import { useState, useEffect } from 'react';
import { useEstimates } from '../../context/EstimateContext';
import SignatureCanvas from '../ui/SignatureCanvas';
import WMIcon from '../ui/WMIcon';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const CATEGORY_LABEL = {
  material: 'Material',
  labor: 'Labor',
  addon: 'Add-on',
  discount: 'Discount',
};

function StatusPill({ status }) {
  const map = {
    draft: 'bg-gray-100 text-gray-600',
    sent: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    declined: 'bg-red-100 text-red-700',
    expired: 'bg-amber-100 text-amber-700',
    converted: 'bg-purple-100 text-purple-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[var(--wm-text-ui)] font-medium ${map[status] || map.draft}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

export default function CustomerEstimateView({ token: propToken }) {
  // Accept token from either prop (public route) or URL (future router integration)
  const token = propToken || window.location.pathname.split('/e/')[1]?.split('/')[0];
  const { getEstimateByShareToken, updateEstimate } = useEstimates();

  const [estimate, setEstimate] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [actionState, setActionState] = useState(null); // 'approving' | 'declining' | 'done'
  const [doneType, setDoneType] = useState(null); // 'approved' | 'declined' — tracks outcome after done
  const [declineReason, setDeclineReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setNotFound(true); return; }
    const est = getEstimateByShareToken(token);
    if (!est) { setNotFound(true); return; }
    setEstimate(est);
    // Mark as viewed if not yet
    if (!est.shareViewedAt) {
      updateEstimate(est.id, { shareViewedAt: new Date().toISOString() });
    }
  }, [token, getEstimateByShareToken, updateEstimate]);

  const handleApprove = async (signature) => {
    setLoading(true);
    try {
      updateEstimate(estimate.id, {
        status: 'approved',
        approvedAt: new Date().toISOString(),
        signature: signature || null,
      });
      setDoneType('approved');
      setActionState('done');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      updateEstimate(estimate.id, {
        status: 'declined',
        declinedAt: new Date().toISOString(),
        declineReason,
      });
      setDoneType('declined');
      setActionState('done');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <WMIcon name="document" className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-[var(--wm-text-heading)] font-bold text-gray-900">Estimate not found</h1>
          <p className="text-[var(--wm-text-body)] text-gray-500">This link may have expired or been removed. Please contact your shop for a new link.</p>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const lineItems = estimate.lineItems ?? [];
  const subtotal = lineItems.reduce((s, li) => s + (li.total ?? 0), 0);
  const isExpired = estimate.expiresAt && new Date(estimate.expiresAt) < new Date();
  const canAct = ['sent', 'draft'].includes(estimate.status) && !isExpired;

  if (actionState === 'done') {
    const approved = doneType === 'approved';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center space-y-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${approved ? 'bg-green-100' : 'bg-red-100'}`}>
            <WMIcon name={approved ? 'check-circle' : 'x-circle'} className={`w-8 h-8 ${approved ? 'text-green-600' : 'text-red-500'}`} />
          </div>
          <h1 className="text-[var(--wm-text-heading)] font-bold text-gray-900">
            {approved ? 'Estimate Approved!' : 'Estimate Declined'}
          </h1>
          <p className="text-[var(--wm-text-body)] text-gray-500">
            {approved
              ? 'Thank you! Your shop has been notified and will be in touch to schedule your service.'
              : 'Thank you for letting us know. Your shop has been notified.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header bar */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <WMIcon name="wrench" className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[var(--wm-text-ui)] text-gray-500">Estimate from your shop</p>
              <p className="text-[var(--wm-text-body)] font-semibold text-gray-900">{estimate.estimateNumber}</p>
            </div>
          </div>
          <StatusPill status={estimate.status} />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Expiry warning */}
        {isExpired && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-[var(--wm-text-body)] text-amber-700">
            <WMIcon name="clock" className="w-4 h-4 flex-shrink-0" />
            This estimate expired on {fmtDate(estimate.expiresAt)}. Please contact your shop for an updated quote.
          </div>
        )}

        {/* Vehicle + Customer card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 bg-blue-600 text-white">
            <h1 className="text-[var(--wm-text-heading)] font-bold">{estimate.vehicleLabel || 'Vehicle Wrap Estimate'}</h1>
            {estimate.vehicleVin && <p className="text-blue-200 text-[var(--wm-text-ui)] mt-0.5">VIN: {estimate.vehicleVin}</p>}
          </div>
          <div className="px-5 py-4 grid grid-cols-2 gap-4 text-[var(--wm-text-body)]">
            <div>
              <p className="text-[var(--wm-text-ui)] text-gray-400 uppercase tracking-wide">Customer</p>
              <p className="font-medium text-gray-900 mt-0.5">{estimate.customerName}</p>
            </div>
            <div>
              <p className="text-[var(--wm-text-ui)] text-gray-400 uppercase tracking-wide">Valid Until</p>
              <p className={`font-medium mt-0.5 ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                {fmtDate(estimate.expiresAt)}
              </p>
            </div>
            {estimate.package && (
              <div>
                <p className="text-[var(--wm-text-ui)] text-gray-400 uppercase tracking-wide">Package</p>
                <p className="font-medium text-gray-900 mt-0.5">{estimate.package}</p>
              </div>
            )}
            {estimate.material && (
              <div>
                <p className="text-[var(--wm-text-ui)] text-gray-400 uppercase tracking-wide">Material</p>
                <p className="font-medium text-gray-900 mt-0.5">{estimate.material} · {estimate.materialColor}</p>
              </div>
            )}
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-[var(--wm-text-body)] font-semibold text-gray-900">Quote Breakdown</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {lineItems.length === 0 ? (
              <div className="px-5 py-6 text-[var(--wm-text-body)] text-gray-400 text-center">No line items.</div>
            ) : (
              lineItems.map(li => (
                <div key={li.id} className="px-5 py-3 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--wm-text-body)] font-medium text-gray-900 truncate">{li.description || '—'}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[var(--wm-text-ui)] text-gray-400">{CATEGORY_LABEL[li.category] || li.category}</span>
                      {li.qty !== 1 && (
                        <span className="text-[var(--wm-text-ui)] text-gray-400">× {li.qty}</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-[var(--wm-text-body)] font-mono font-semibold flex-shrink-0 ${li.category === 'discount' ? 'text-red-600' : 'text-gray-900'}`}>
                    {li.category === 'discount' ? `-${fmt(Math.abs(li.total))}` : fmt(li.total)}
                  </span>
                </div>
              ))
            )}
          </div>
          {/* Total */}
          <div className="px-5 py-4 bg-blue-50 border-t-2 border-blue-600 flex justify-between items-center">
            <span className="text-[var(--wm-text-body)] font-semibold text-gray-900">Total</span>
            <span className="text-[var(--wm-text-heading)] font-bold font-mono text-blue-600">{fmt(subtotal)}</span>
          </div>
        </div>

        {/* Notes */}
        {estimate.notes && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-5 py-4">
            <h3 className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide text-gray-400 mb-2">Notes from your shop</h3>
            <p className="text-[var(--wm-text-body)] text-gray-700">{estimate.notes}</p>
          </div>
        )}

        {/* Existing signature */}
        {estimate.signature && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-5 py-4">
            <h3 className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide text-gray-400 mb-3">Your Signature</h3>
            <SignatureCanvas existingSignature={estimate.signature} readOnly />
          </div>
        )}

        {/* Action buttons */}
        {canAct && !estimate.signature && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 space-y-4">
            <h3 className="text-[var(--wm-text-body)] font-semibold text-gray-900">Ready to proceed?</h3>

            {actionState === 'approving' ? (
              <div className="space-y-4">
                <p className="text-[var(--wm-text-ui)] text-gray-500">Please sign below to approve this estimate.</p>
                <SignatureCanvas
                  onSave={(sig) => handleApprove(sig)}
                  onCancel={() => setActionState(null)}
                  signerNameLabel="Your Full Name"
                />
              </div>
            ) : actionState === 'declining' ? (
              <div className="space-y-3">
                <div>
                  <label className="text-[var(--wm-text-ui)] font-medium text-gray-600 block mb-1">Reason (optional)</label>
                  <textarea
                    value={declineReason}
                    onChange={e => setDeclineReason(e.target.value)}
                    placeholder="Help us understand why you're declining…"
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[var(--wm-text-body)] focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setActionState(null)} className="flex-1 py-2 text-[var(--wm-text-body)] border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
                    Back
                  </button>
                  <button
                    onClick={handleDecline}
                    disabled={loading}
                    className="flex-1 py-2 text-[var(--wm-text-body)] bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? 'Declining…' : 'Confirm Decline'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => setActionState('approving')}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <WMIcon name="check-circle" className="w-4 h-4" />
                  Approve & Sign
                </button>
                <button
                  onClick={() => setActionState('declining')}
                  className="flex-1 py-3 border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                >
                  <WMIcon name="x-circle" className="w-4 h-4" />
                  Decline
                </button>
              </div>
            )}

            {error && <p className="text-[var(--wm-text-body)] text-red-600">{error}</p>}
          </div>
        )}

        {/* Already actioned */}
        {!canAct && estimate.status === 'approved' && (
          <div className="bg-green-50 border border-green-200 rounded-2xl px-5 py-4 flex items-center gap-3 text-green-700">
            <WMIcon name="check-circle" className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-[var(--wm-text-body)] font-semibold">Approved</p>
              {estimate.approvedAt && <p className="text-[var(--wm-text-ui)] mt-0.5 text-green-600">{fmtDate(estimate.approvedAt)}</p>}
            </div>
          </div>
        )}
        {!canAct && estimate.status === 'declined' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 flex items-center gap-3 text-red-700">
            <WMIcon name="x-circle" className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-[var(--wm-text-body)] font-semibold">Declined</p>
              {estimate.declinedAt && <p className="text-[var(--wm-text-ui)] mt-0.5 text-red-500">{fmtDate(estimate.declinedAt)}</p>}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-[var(--wm-text-ui)] text-gray-400 pb-8">
          Powered by WrapMind · {estimate.estimateNumber}
        </p>
      </main>
    </div>
  );
}
